import React, { useEffect, useState } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Header, Loader, Sidebar } from 'components';
import { routes } from 'enums';
import styles from './PrivateLayout.module.scss';
import { useActions, useAppSelector } from 'hooks';
import { accessTokenSelect, authThunk, initializedSelect, isLoadingSelect } from 'state';
import { readStoredTokens } from 'config';
import Burger from 'components/Burger/Burger';
import Menu from 'components/Menu/Menu';

const useOnClickOutside = (ref: any, handler: any) => {
  React.useEffect(() => {
    const listener = (event: any) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener('mousedown', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
    };
  }, [ref, handler]);
};

export const PrivateLayout = () => {
  const location = useLocation();
  const token = useAppSelector(accessTokenSelect);
  // Was `const initialized = true`, which made the guard below dead code. The
  // real flag is set once getMe settles, so the layout no longer decides
  // whether the session is valid before the answer has arrived.
  const initialized = useAppSelector(initializedSelect);
  const { getMe } = useActions(authThunk);
  const isLoading = useAppSelector(isLoadingSelect);
  // Redux is empty on a fresh page load, so the presence of a persisted token
  // is what distinguishes "restoring a session" from "never logged in".
  const [hasStoredToken] = useState(() => !!readStoredTokens().accessToken);
  const [isBurgerMenuOpen, setIsBurgerMenuOpen]: any = useState(false);

  const [open, setOpen] = React.useState(false);
  const node: any = React.useRef();
  useOnClickOutside(node, () => setOpen(false));

  useEffect(() => {
    // Calling getMe without a token guarantees a 401; skip straight to /login.
    if (hasStoredToken) {
      getMe();
    }
  }, []);

  if (!hasStoredToken) {
    return <Navigate to={routes.AUTH.LOG_IN} replace state={location} />;
  }

  if (!initialized || isLoading) {
    return <Loader />;
  }

  const handleOpenBurgerMenu = () => {
    setIsBurgerMenuOpen(!isBurgerMenuOpen);
  };

  return token ? (
    <div className={styles.privateLayout}>
      <Sidebar />

      {open && <div className={styles.shadowContainer}></div>}

      <div ref={node}>
        <div className={styles.burgerContainer}>
          <Burger open={open} setOpen={setOpen} />
        </div>

        <Menu open={open} />
      </div>

      {/* Header and page share one column.
          The header used to be positioned absolutely against the viewport at
          `calc(100vw - 250px)` -- a width that disagreed with the sidebar's
          real one, so its left border never lined up with the sidebar's right
          border. Laying it out as the first row of the content column means
          the alignment is structural: there is no number to get wrong. */}
      <div className={styles.contentColumn}>
        <Header />

        <div className={styles.outletContainer}>
          <Outlet />
        </div>
      </div>
    </div>
  ) : (
    <Navigate to={routes.AUTH.LOG_IN} replace state={location} />
  );
};
